<template>
  <EditorDeckSlidesThreeDLayerListItem
    class="label-layer group h-9"
    :selected-in-panel="selectedInPanel"
    :visible="label.visible"
    :focused="hasFocus"
    :selected-in-scene="selectedInScene"
    :class="{ 'handle-multi-line': hasFocus }"
    @set-visible="setVisibility"
    @contextmenu.stop.prevent="!disabled && $emit('contextmenu', $event)"
    @click="$emit('click', $event)"
  >
    <div
      v-if="label.content.length"
      ref="container"
      class="flex items-center w-full h-full ml-6 layer-title-container gap-2"
      data-testid="label"
      :class="{ 'pointer-events-none': disabled || isEditingIndividualScene || isDragging, 'transition-none': isDragging }"
      draggable="true"
      @dragstart="handleDragStart"
      @dragend="handleDragEnd"
    >
      <TypeIcon class="flex-shrink-0 w-3 h-3 text-copper-200" data-testid="label-type-icon" @click.stop="focusEditable" />
      <span v-if="!!readonly" class="truncate max-w-4/5">{{ labelText }}</span>
      <InlineEditable
        v-else
        v-model:value="labelText"
        class="min-w-0 w-0 flex-grow px-0 focus-within:px-2 hover:px-2 my-0.5"
        theme="dark"
        allow-line-break
        data-testid="label-editable"
        @save="save"
        @focus="focus"
        @blur=";(hasFocus = false), (labelText = toReturnSymbol(labelText))"
        @clear="remove"
        @cancel="remove"
      />
      <div v-if="!readonly" class="drag-handle-container">
        <svg class="w-4 h-4 pointer-events-none" viewBox="0 0 20 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="4" cy="4" r="4" fill="currentColor" />
          <circle cx="4" cy="16" r="4" fill="currentColor" />
          <circle cx="4" cy="28" r="4" fill="currentColor" />
          <circle cx="16" cy="4" r="4" fill="currentColor" />
          <circle cx="16" cy="16" r="4" fill="currentColor" />
          <circle cx="16" cy="28" r="4" fill="currentColor" />
        </svg>
        <div class="absolute top-0 left-0 w-full h-full pointer-events-auto drag-handle" />
      </div>
    </div>
  </EditorDeckSlidesThreeDLayerListItem>
</template>

<script lang="ts" setup>
import TypeIcon from '@/components/icons/TypeIcon.svg'
import { toMultiLine, toReturnSymbol } from '@/lib/three-d-editor/utils'
import type { SceneElement } from '@/entities/model3d'
import type { TextLabel, LabelsManager } from '~/lib/three-d-resources/labels-manager'

const props = defineProps({
  label: {
    type: Object as PropType<TextLabel>,
    required: true,
  },
  labelsManager: {
    type: Object as PropType<LabelsManager>,
    required: true,
  },
  selectedInPanel: {
    type: Boolean,
    default: false,
  },
  scopingElement: {
    type: Object as PropType<SceneElement | null>,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: [String, Boolean],
    default: false,
  },
  isEditingIndividualScene: {
    type: Boolean,
    default: false,
  },
  selectedInScene: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click', 'contextmenu', 'set-visible', 'vrify-dragstart', 'vrify-dragend'])

const labelText = ref(toReturnSymbol(props.label.content))
const hasFocus = ref(false)
const container = ref<HTMLElement | null>(null)
const isDragging = ref(false)

function save() {
  props.labelsManager.updateLabel(props.label.id, {
    content: toMultiLine(labelText.value),
  })
}

async function remove() {
  props.labelsManager.removeLabel(props.label.id)
}

function focusEditable(e: MouseEvent) {
  focus(e)
  container.value!.querySelector<HTMLElement>('.editable')!.focus()
}

function blurEditable() {
  document.querySelector<HTMLElement>('.text-label .selected .editable')?.blur()
}

function focus(_e: Event) {
  hasFocus.value = true
  labelText.value = toMultiLine(labelText.value)
}

function setVisibility(visible: boolean): void {
  if (!props.disabled) {
    // VRFY-11502
    // ensure blur is triggered on inline editable first
    // otherwise when the label is unhidden, the inline editable will never have content
    blurEditable()
    emit('set-visible', visible)
  }
}

function handleDragStart(e: DragEvent) {
  const el = document.elementFromPoint(e.clientX, e.clientY)

  if (!el?.classList.contains('drag-handle')) {
    e.preventDefault()
    e.stopPropagation()
    return
  }

  isDragging.value = true
  emit('vrify-dragstart', props.label.id)
}

function handleDragEnd(e: DragEvent) {
  isDragging.value = false
  emit('vrify-dragend')
}

watch(
  () => props.label.content,
  content => {
    labelText.value = toReturnSymbol(content)
  }
)
</script>

<style lang="stylus" scoped>
.label-layer
  .drag-handle-container
    @apply absolute top-0 right-0 h-full w-6 flex items-center justify-center pointer-events-none cursor-move
    .drag-handle
      @apply w-full h-full pointer-events-auto

.label-layer .ui-inline-editable
  @apply whitespace-nowrap overflow-hidden overflow-ellipsis

.handle-multi-line
  @apply h-auto
  min-height: 2.25rem
</style>
